import React from "react";
import PropTypes from "prop-types";
import { Icon, Input } from "antd";
import { hasCreditRemark } from "../cam-utils";

function OutputPill({ output, remarked }) {
    const isPass = output === "Pass";
    const isFail = output === "Fail";
    const tone = isPass ? "pass" : isFail ? "fail" : "na";
    // Once a failure carries a credit remark it no longer prompts for action,
    // so the tag drops away and only the Fail pill remains.
    const showTag = isPass || (isFail && !remarked);
    return (
        <span className={"cc-cam-out"}>
            <span className={"cc-cam-out__pill cc-cam-out__pill--" + tone}>
                {output}
            </span>
            {showTag ? (
                <span
                    className={
                        "cc-cam-out__tag cc-cam-out__tag--" +
                        (isPass ? "ok" : "act")
                    }
                >
                    <Icon
                        type={isPass ? "check-circle" : "exclamation-circle"}
                    />
                    {isPass ? "Approved" : "Requires Action"}
                </span>
            ) : null}
        </span>
    );
}

OutputPill.propTypes = { output: PropTypes.string, remarked: PropTypes.bool };

/**
 * Parameter table for one CAM category: each row shows the policy rule, the
 * actual value, and the pass/fail output — the reviewer sees rule + value +
 * verdict together.
 */
const CamParameterTable = ({ rows, editable, onEditRemark }) => (
    <div className={"cc-cam-table-wrap"}>
        <table className={"cc-cam-table"}>
            <thead>
                <tr>
                    <th className={"cc-cam-table__sno"}>#</th>
                    <th>Parameter</th>
                    <th>Policy</th>
                    <th>Value</th>
                    <th>Output</th>
                    <th>Credit Remarks</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row, i) => {
                    const isNA = row.output === "Not Applicable";
                    return (
                    <tr
                        key={i}
                        className={
                            isNA
                                ? "cc-cam-table__row--na"
                                : row.output === "Fail"
                                    ? "cc-cam-table__row--fail"
                                    : ""
                        }
                    >
                        <td className={"cc-cam-table__sno"}>{i + 1}</td>
                        <td className={"cc-cam-table__param"}>{row.factName}</td>
                        <td className={"cc-cam-table__policy"}>{row.policy}</td>
                        <td
                            className={"cc-cam-table__value"}
                            title={row.value}
                        >
                            {row.value}
                        </td>
                        <td>
                            <OutputPill
                                output={row.output}
                                remarked={hasCreditRemark(row)}
                            />
                        </td>
                        <td
                            className={"cc-cam-table__remarks"}
                            title={editable ? undefined : row.creditRemarks || ""}
                        >
                            {editable && !isNA ? (
                                // Fixed rows (no autoSize): an autoSize textarea
                                // re-measures whenever the container width
                                // changes, which forms a scrollbar-toggle
                                // feedback loop with the scroll container and
                                // makes the columns jitter left/right.
                                <Input.TextArea
                                    value={row.creditRemarks || ""}
                                    rows={2}
                                    placeholder={"Add remark…"}
                                    onChange={e =>
                                        onEditRemark &&
                                        onEditRemark(row.key2, e.target.value)
                                    }
                                />
                            ) : row.creditRemarks ? (
                                row.creditRemarks
                            ) : (
                                <span className={"cc-kyc-empty"}>—</span>
                            )}
                        </td>
                    </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

CamParameterTable.propTypes = {
    rows: PropTypes.array.isRequired,
    editable: PropTypes.bool,
    onEditRemark: PropTypes.func
};

export default CamParameterTable;
